// sim/leaderboard.ts — the colony-evolution LEADERBOARD: a read-only aggregation
// layer that scores every colony's evolutionary progress and ranks them.
//
// WHAT THIS IS (and is NOT):
//   This is an OBSERVER. It reads the public accessors of the colony, colony-AI,
//   and gene-inventory systems and writes ONLY its own per-colony score columns.
//   It runs LAST in the tick order, so it can never perturb another system's
//   behaviour or the RNG sequence — same seed + ticks ⇒ identical standings.
//
// TWO PARTS:
//   1. PURE SCORERS — exported, allocation-free, integer functions that turn a
//      handful of integers into one dimension's subscore. Unit-testable with no
//      world. These are the contract; the system just feeds them world state.
//   2. THE SYSTEM — `LeaderboardSystem` (a `System`): on a deterministic cadence
//      it recomputes all six subscores per colony, folds them by config weight
//      into an instant score, accumulates lifetime + peak, derives a total, and
//      ranks colonies by total desc (tie-break cid asc).
//
// DATA-ORIENTED + NO HOT-PATH ALLOCATION (DESIGN.md §2 / EFFICIENCY.md §B):
//   Every store is a preallocated typed array sized to `colonies.capacity`. The
//   recompute reuses a single `rarityScratch` array and an in-place insertion
//   sort over a preallocated `rankOrder` buffer — `update` allocates nothing.
//
// DETERMINISM:
//   Pure integer state (Int32 throughout — scores never go float). No RNG. Fixed
//   iteration order (colony id order; gene walk is the inventory's deterministic
//   insertion order). The standings fold into computeColonyStateHash.

import { NIL, RARITY_COUNT, GENE_TYPE_COUNT, SCORE_DIM, SCORE_DIM_COUNT } from '../types.ts';
import type {
  System,
  Tick,
  ColonyId,
  ScoreDim,
  LeaderboardConfig,
  DiscoveryRenownSource,
} from '../types.ts';
import type { ColonySystem } from './colony.ts';
import type { ColonyAISystem } from './colonyAI.ts';
import type { GeneInventory } from './genes.ts';

// ---------------------------------------------------------------------------
// Optional REAL-signal seams (mirror DiscoveryRenownSource): zero-dependency
// readers the leaderboard folds into a dimension WHEN a source is late-bound.
// With NO source wired each dimension falls back to its proxy formula, so the
// stored subscore is BYTE-IDENTICAL to before — every existing determinism test
// (and the canonical buildColonyWorld / buildEcosystemWorld) is unaffected. Only
// web/game.ts wires these (after the world + rivals + pressure exist).
// ---------------------------------------------------------------------------

/**
 * Real per-colony ecological-pressure magnitude that upgrades the EcosystemInfluence
 * dimension's PROXY (food share) to a TRUE signal. When wired, the colony's share of
 * this quantity across the world replaces the food-share term; unwired ⇒ proxy.
 */
export interface EcosystemInfluenceSource {
  /** Real per-colony ecological pressure magnitude (predation+aerial+theft absorbed); non-negative int. */
  influenceOf(cid: ColonyId): number;
}

/**
 * Real per-colony region-claim count that upgrades the RegionDominance dimension's
 * PROXY (competitive-strength supremacy) to a TRUE signal. When wired, the colony's
 * regions-held count (vs the world peak) replaces the strength proxy; unwired ⇒ proxy.
 */
export interface RegionDominanceSource {
  /** Real per-colony region-claim count. */
  regionsHeldOf(cid: ColonyId): number;
}

// ---------------------------------------------------------------------------
// Pure scoring functions — integer, allocation-free, world-free (unit-testable).
// ---------------------------------------------------------------------------

/**
 * Per-dimension subscore SOFT-CAP — the safety net against a runaway INSTANT
 * score (the GeneRarity/GenomeComplexity sibling of the `lifetime` LIFETIME_CAP).
 *
 * WHY THIS EXISTS:
 *   Autonomous gene splicing (colonyAI.evolve / rivals.evolve / the hive Mutation
 *   chamber) sums BOTH parents' trait stacks into the child, so a colony's
 *   `traitTotal` grows Fibonacci-fast over a long run — gen ~40 reaches a single
 *   gene with traitTotal in the hundreds of MILLIONS, and the per-colony sum tops
 *   ~1.8e9. GenomeComplexity feeds that raw `traitStacksTotal` (and `maxGeneration`)
 *   straight into its subscore, so on the worst seeds the weighted instant total
 *   reached hundreds of millions (approaching Int32_MAX) — a >10,000x spread across
 *   seeds at 3000 ticks. `lifetime` was already capped (LIFETIME_CAP); the INSTANT
 *   subscores were not. This bounds them with the SAME discipline geneExpr.ts
 *   (`softCapChannel`) already applies to channel magnitudes.
 *
 * THE CURVE (integer, non-negative, monotonic):
 *   At/below SUBSCORE_SOFT_CAP_THRESHOLD a subscore passes through UNCHANGED, so
 *   every normal colony's subscores (measured: each dimension stays well under ~200
 *   for any seed at ≤1000 ticks) are BYTE-IDENTICAL to the pre-cap output — the cap
 *   never perturbs ordinary play. Past the threshold the EXCESS is compressed
 *   (halved) and the result is hard-clamped at SUBSCORE_SOFT_CAP_CEILING, so a
 *   pathological deeply-spliced gene can no longer push one dimension arbitrarily
 *   high. Diminishing returns, not a hard wall: a colony a little over the
 *   threshold still scores a little more than one exactly at it (ordering preserved).
 */

/**
 * Subscore magnitude at/below which a dimension's value is returned UNCHANGED.
 * Chosen ~3-9x above the largest subscore any seed produces in normal play
 * (the biggest observed normal dimension is EcosystemInfluence at ~174 over a
 * 1000-tick run), so the cap is a pure safety net for the runaway-splice case
 * and never touches an ordinary colony's standings.
 */
export const SUBSCORE_SOFT_CAP_THRESHOLD = 600;

/**
 * Hard ceiling on a single dimension's subscore after compression. No matter how
 * deeply a colony's genes are spliced, one dimension can contribute at most this.
 * With the default weights (Σ = 20) the worst-case weighted instant is therefore
 * 20 * 1200 = 24_000 — tiny next to the Int32 ceiling, and the amortised lifetime
 * term (≤ floor(LIFETIME_CAP / lifetimeDivisor)) stays the dominant, already-bounded
 * contributor, so `total` is comfortably Int32 over arbitrarily long runs.
 */
export const SUBSCORE_SOFT_CAP_CEILING = 1200;

/**
 * Soft-cap one non-negative subscore with diminishing returns above the
 * threshold, hard-clamped at the ceiling. Integer math:
 *   v <= THRESHOLD  ⇒ v unchanged
 *   v >  THRESHOLD  ⇒ min(CEILING, THRESHOLD + (v - THRESHOLD) / 2)
 * Monotonic non-decreasing in v; allocation-free. Negative inputs (which the
 * scorers never produce) are floored at 0 so the result is always non-negative.
 */
export function softCapSubscore(v: number): number {
  if (v <= 0) return 0;
  if (v <= SUBSCORE_SOFT_CAP_THRESHOLD) return v;
  let capped = SUBSCORE_SOFT_CAP_THRESHOLD + ((v - SUBSCORE_SOFT_CAP_THRESHOLD) >> 1);
  if (capped > SUBSCORE_SOFT_CAP_CEILING) capped = SUBSCORE_SOFT_CAP_CEILING;
  return capped;
}

/**
 * Score weight per rarity tier, indexed by RARITY.* (Common→Legendary). Rarer
 * genes are worth exponentially more, so a single Legendary outweighs a pile of
 * Commons — rarity is the headline signal of evolutionary investment.
 */
export const RARITY_SCORE: readonly number[] = [1, 2, 4, 8, 16];

/**
 * Strength throttle mirroring regions.ts: a colony's competitive "strength" is
 * `population + floor(food / FOOD_PER_STRENGTH)`. Defined locally (NOT imported
 * from regions.ts) to keep this module dependency-light — it only ever needs the
 * constant, not the manager.
 */
export const FOOD_PER_STRENGTH = 5;

/**
 * Genome complexity: how elaborate the colony's gene pool is. Distinct body-part
 * types dominate (breadth of evolution), trait stacks add depth, and splice
 * generation rewards a deep lineage. All integer.
 */
export function scoreGenomeComplexity(
  distinctTypes: number,
  traitStacksTotal: number,
  maxGeneration: number,
): number {
  return distinctTypes * 5 + traitStacksTotal * 1 + maxGeneration * 3;
}

/**
 * Gene rarity: Σ rarityCounts[r] * RARITY_SCORE[r] over the RARITY_COUNT tiers.
 * Accepts any indexable integer vector (a `number[]` from a test, or the
 * system's reused `Int32Array` scratch) so the hot path passes its scratch with
 * zero copying.
 */
export function scoreGeneRarity(rarityCounts: ArrayLike<number>): number {
  let s = 0;
  for (let r = 0; r < RARITY_COUNT; r++) {
    s += (rarityCounts[r] as number) * (RARITY_SCORE[r] as number);
  }
  return s;
}

/** Territory control: the home nest counts as 1, each founded hive node as 4. */
export function scoreTerritoryControl(hiveNodes: number): number {
  return 1 + hiveNodes * 4;
}

/** Colony size: raw population, weighted. */
export function scoreColonySize(population: number): number {
  return population * 2;
}

/**
 * Ecosystem influence: the colony's ecological footprint as a share of the
 * finite contested world. Both inputs are already 0..100 percentages (biomass
 * share and food share), so the sum is a 0..200 footprint score.
 */
export function scoreEcosystemInfluence(biomassSharePct: number, foodSharePct: number): number {
  return biomassSharePct + foodSharePct;
}

/**
 * Region dominance: how close this colony's strength is to the strongest colony
 * in the world, scaled to 0..100. The leader (strength == peak) scores 100; a
 * half-strength colony scores 50. Guards a zero/negative peak.
 */
export function scoreRegionDominance(strength: number, peakStrength: number): number {
  return peakStrength <= 0 ? 0 : Math.floor((strength * 100) / peakStrength);
}

/**
 * Discovery: emergent-morph renown a colony has earned through the Evolution-
 * Discovery observer (sticky discoverer credit + live expressed-morph renown).
 * Clamped at zero so a missing/negative source can never push a score below 0.
 */
export function scoreDiscovery(renown: number): number {
  return renown > 0 ? renown : 0;
}

/**
 * Saturation ceiling for the per-colony `lifetime` accumulator. `lifetime` is a
 * running sum of every (non-negative) instant score, stored Int32; over a very
 * long run an unguarded sum would wrap past Int32_MAX into negative and corrupt
 * standings. Capping below Int32_MAX (with headroom for `total = inst +
 * floor(lifetime / lifetimeDivisor)` to stay Int32 too) makes the sum saturate
 * instead — standings calcify rather than break.
 */
export const LIFETIME_CAP = 2_000_000_000;

/**
 * Saturating add for the lifetime accumulator: `current + inst`, clamped at
 * LIFETIME_CAP. Both inputs are non-negative (instants are weighted sums of
 * non-negative subscores), so the sum is monotonic and only ever needs a ceiling.
 */
export function addLifetime(current: number, inst: number): number {
  const sum = current + inst;
  return sum > LIFETIME_CAP ? LIFETIME_CAP : sum;
}

// ---------------------------------------------------------------------------
// The tracking system.
// ---------------------------------------------------------------------------

export class LeaderboardSystem implements System {
  readonly name = 'leaderboard';

  private readonly colonies: ColonySystem;
  private readonly ai: ColonyAISystem;
  private readonly genes: GeneInventory;
  private readonly config: LeaderboardConfig;
  /** Optional Evolution-Discovery renown source (null ⇒ Discovery dim is 0). */
  private readonly discovery: DiscoveryRenownSource | null;
  /**
   * Optional REAL ecological-pressure source (null ⇒ EcosystemInfluence uses its
   * food-share PROXY exactly as before). Late-bound via `attachEcosystemInfluence`.
   */
  private ecoSignal: EcosystemInfluenceSource | null = null;
  /**
   * Optional REAL region-claim source (null ⇒ RegionDominance uses its strength
   * PROXY exactly as before). Late-bound via `attachRegionDominance`.
   */
  private regionSignal: RegionDominanceSource | null = null;

  readonly capacity: number;

  // --- Per-colony score columns (SoA, preallocated, Int32). ---
  /** Raw per-dimension subscores: index [cid * SCORE_DIM_COUNT + dim]. */
  private readonly subscore: Int32Array;
  /** This update's weighted score (Σ weight[d] * subscore[d]). */
  private readonly instant: Int32Array;
  /** Highest instant score ever reached (non-decreasing). */
  private readonly peak: Int32Array;
  /** Running sum of every instant score (long-term progression). */
  private readonly lifetime: Int32Array;
  /** instant + floor(lifetime / lifetimeDivisor) — the ranked figure. */
  private readonly total: Int32Array;
  /** Standing of each colony (0 == leader). */
  private readonly rank: Int32Array;

  // --- Scratch (reused per update; never allocates in the hot path). ---
  /** Colony ids being sorted, in-place, by total desc. */
  private readonly rankOrder: Int32Array;
  /** Per-rarity tally for the current colony's gene walk. */
  private readonly rarityScratch: Int32Array;
  /** Per-colony attached-influence cache: filled in the normaliser pre-pass, read
   *  again in the per-colony share pass so `influenceOf` is called once, not twice. */
  private readonly influenceScratch: Int32Array;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  updatesTotal = 0;

  constructor(
    colonies: ColonySystem,
    ai: ColonyAISystem,
    genes: GeneInventory,
    config: LeaderboardConfig,
    discovery?: DiscoveryRenownSource,
  ) {
    this.colonies = colonies;
    this.ai = ai;
    this.genes = genes;
    this.config = config;
    this.discovery = discovery ?? null;
    this.capacity = colonies.capacity;

    this.subscore = new Int32Array(this.capacity * SCORE_DIM_COUNT);
    this.instant = new Int32Array(this.capacity);
    this.peak = new Int32Array(this.capacity);
    this.lifetime = new Int32Array(this.capacity);
    this.total = new Int32Array(this.capacity);
    this.rank = new Int32Array(this.capacity);

    this.rankOrder = new Int32Array(this.capacity);
    this.rarityScratch = new Int32Array(RARITY_COUNT);
    this.influenceScratch = new Int32Array(this.capacity);
  }

  // --- Late-bound REAL-signal seams (mirror the discovery source) ------------
  // These are wired AFTER the world is built (the sources live in systems that
  // aren't part of the canonical world), so they must be attach methods, NOT
  // constructor params — that keeps buildColonyWorld / buildEcosystemWorld
  // byte-identical and every existing determinism test green. Until attached the
  // matching dimension computes EXACTLY as before (same pure scorer, same inputs).

  /**
   * Late-bind the REAL ecological-pressure source. Once attached, EcosystemInfluence's
   * second term becomes the colony's SHARE of total world ecological pressure instead
   * of its food share (biomass-share term unchanged). Unattached ⇒ food-share proxy.
   */
  attachEcosystemInfluence(src: EcosystemInfluenceSource): void {
    this.ecoSignal = src;
  }

  /**
   * Late-bind the REAL region-claim source. Once attached, RegionDominance scores the
   * colony's regions-held count against the world peak instead of competitive strength.
   * Unattached ⇒ strength proxy.
   */
  attachRegionDominance(src: RegionDominanceSource): void {
    this.regionSignal = src;
  }

  // --- Per-tick: recompute standings on the cadence --------------------------

  update(tick: Tick): void {
    // Cadence gate (deterministic; the determinism tests guard the off-by-one).
    if (tick % this.config.cadence !== 0) return;

    const colonies = this.colonies;
    const ai = this.ai;
    const genes = this.genes;
    const weights = this.config.weights;
    const count = colonies.count;

    // Pre-pass: world totals + the strongest colony (for share/dominance scores).
    // When a REAL-signal source is attached we ALSO accumulate its normaliser here
    // (total ecological pressure for the influence SHARE; peak regions-held for the
    // dominance scale) — guarded so the source's accessor is never even called, and
    // nothing is summed, when unattached ⇒ the proxy path is byte-identical.
    const ecoSignal = this.ecoSignal;
    const regionSignal = this.regionSignal;
    let worldPop = 0;
    let worldFood = 0;
    let peakStrength = 0;
    let totalInfluence = 0;
    let peakRegions = 0;
    for (let cid = 0; cid < count; cid++) {
      const pop = colonies.populationAt(cid);
      const food = colonies.foodAt(cid);
      worldPop += pop;
      worldFood += food;
      const strength = pop + Math.floor(food / FOOD_PER_STRENGTH);
      if (strength > peakStrength) peakStrength = strength;
      if (ecoSignal) {
        // Cache the per-colony influence so the per-colony share pass below reads it
        // without a SECOND hook call. The source is an integer pressure magnitude, so
        // the Int32 cache equals the raw value ⇒ identical scores (byte-identical).
        this.influenceScratch[cid] = ecoSignal.influenceOf(cid);
        totalInfluence += this.influenceScratch[cid] as number;
      }
      if (regionSignal) {
        const held = regionSignal.regionsHeldOf(cid);
        if (held > peakRegions) peakRegions = held;
      }
    }

    for (let cid = 0; cid < count; cid++) {
      const pop = colonies.populationAt(cid);
      const food = colonies.foodAt(cid);

      // --- GenomeComplexity + GeneRarity: ONE walk of the colony's genes. ---
      this.rarityScratch.fill(0);
      let typeMask = 0;
      let traitStacks = 0;
      let maxGen = 0;
      for (let gid = genes.firstOf(cid); gid !== NIL; gid = genes.nextOf(gid)) {
        typeMask |= 1 << genes.typeOf(gid);
        traitStacks += genes.traitTotalOf(gid);
        const g = genes.generationOf(gid);
        if (g > maxGen) maxGen = g;
        this.rarityScratch[genes.rarityOf(gid)] =
          (this.rarityScratch[genes.rarityOf(gid)] as number) + 1;
      }
      // popcount over the GENE_TYPE_COUNT low bits (tiny fixed loop).
      let distinctTypes = 0;
      for (let b = 0; b < GENE_TYPE_COUNT; b++) {
        if ((typeMask & (1 << b)) !== 0) distinctTypes++;
      }
      const sGenome = scoreGenomeComplexity(distinctTypes, traitStacks, maxGen);
      const sRarity = scoreGeneRarity(this.rarityScratch);

      // --- TerritoryControl: home nest + founded hive nodes. ---
      const sTerritory = scoreTerritoryControl(ai.hiveCountAt(cid));

      // --- ColonySize: raw population. ---
      const sSize = scoreColonySize(pop);

      // --- EcosystemInfluence: biomass share + ecological-footprint share. The
      //     biomass-share term (population share of the contested world) is always
      //     real. The SECOND term is the colony's ecological-pressure SHARE when an
      //     EcosystemInfluenceSource is attached (web/game.ts wires world.pressure:
      //     predation+aerial+theft absorbed) — REAL predation / raid pressure, the
      //     signal this PROXY always stood in for. With NO source wired it falls
      //     back to the food-share PROXY, byte-identical to before. Both terms feed
      //     the UNCHANGED scoreEcosystemInfluence (a + b), so the unit scorer and its
      //     test are untouched — only the inputs differ when a source is present. ---
      const biomassPct = worldPop <= 0 ? 0 : Math.floor((pop * 100) / worldPop);
      const sEco = ecoSignal
        ? scoreEcosystemInfluence(
            biomassPct,
            totalInfluence <= 0
              ? 0
              : Math.floor(((this.influenceScratch[cid] as number) * 100) / totalInfluence),
          )
        : scoreEcosystemInfluence(
            biomassPct,
            worldFood <= 0 ? 0 : Math.floor((food * 100) / worldFood),
          );

      // --- RegionDominance: when a RegionDominanceSource is attached (web/game.ts
      //     wires RivalSystem.regionsHeldBy) this scores the colony's REAL region-
      //     claim count against the world peak — the actual territory supremacy this
      //     PROXY always stood in for. With NO source wired it falls back to the
      //     competitive-strength PROXY (pop + floor(food/FOOD_PER_STRENGTH) vs the
      //     strongest colony), byte-identical to before. Both reuse the UNCHANGED
      //     scoreRegionDominance — only its inputs differ when a source is present. ---
      const strength = pop + Math.floor(food / FOOD_PER_STRENGTH);
      const sRegion = regionSignal
        ? scoreRegionDominance(regionSignal.regionsHeldOf(cid), peakRegions)
        : scoreRegionDominance(strength, peakStrength);

      // --- Discovery (OPTIONAL SEAM): emergent-morph renown from the Evolution-
      //     Discovery observer. When no source is wired the dimension is 0, so
      //     existing scores are unchanged. The discovery system runs BEFORE this
      //     board in the tick order, so the renown read here is same-tick fresh. ---
      const sDiscovery = this.discovery ? scoreDiscovery(this.discovery.renownAt(cid)) : 0;

      // Store the seven subscores, each SOFT-CAPPED so a single runaway input (a
      // deeply-spliced gene with traitTotal in the millions feeding GenomeComplexity)
      // can't push a dimension — and thus the weighted instant — arbitrarily high.
      // The cap is a pure pass-through for every normal-play magnitude (see
      // softCapSubscore), so ordinary standings are byte-identical; it only bites at
      // the pathological splice scale. Capping at the store is the single chokepoint
      // both the hashed `subscore[]` and the weighted `instant` fold below read.
      const base = cid * SCORE_DIM_COUNT;
      this.subscore[base + SCORE_DIM.GenomeComplexity] = softCapSubscore(sGenome);
      this.subscore[base + SCORE_DIM.GeneRarity] = softCapSubscore(sRarity);
      this.subscore[base + SCORE_DIM.TerritoryControl] = softCapSubscore(sTerritory);
      this.subscore[base + SCORE_DIM.ColonySize] = softCapSubscore(sSize);
      this.subscore[base + SCORE_DIM.EcosystemInfluence] = softCapSubscore(sEco);
      this.subscore[base + SCORE_DIM.RegionDominance] = softCapSubscore(sRegion);
      this.subscore[base + SCORE_DIM.Discovery] = softCapSubscore(sDiscovery);

      // Weighted instant score, then lifetime / peak / total.
      let inst = 0;
      for (let d = 0; d < SCORE_DIM_COUNT; d++) {
        inst += (weights[d] as number) * (this.subscore[base + d] as number);
      }
      this.instant[cid] = inst;
      // Saturating accumulate so a very long run can't wrap the Int32 sum negative.
      const life = addLifetime(this.lifetime[cid] as number, inst);
      this.lifetime[cid] = life;
      if (inst > (this.peak[cid] as number)) this.peak[cid] = inst;
      this.total[cid] = inst + Math.floor(life / this.config.lifetimeDivisor);
    }

    // --- Rank: insertion sort rankOrder by total DESC, tie-break cid ASC. ---
    // O(count^2) is fine — colonies are FEW — and it allocates nothing.
    for (let i = 0; i < count; i++) this.rankOrder[i] = i;
    for (let i = 1; i < count; i++) {
      const cur = this.rankOrder[i] as number;
      const curTotal = this.total[cur] as number;
      let j = i - 1;
      while (j >= 0) {
        const other = this.rankOrder[j] as number;
        const otherTotal = this.total[other] as number;
        // Keep `other` ahead when it ranks higher: greater total, or equal total
        // with a lower cid. Otherwise shift it right to make room for `cur`.
        if (otherTotal > curTotal || (otherTotal === curTotal && other < cur)) break;
        this.rankOrder[j + 1] = other;
        j--;
      }
      this.rankOrder[j + 1] = cur;
    }
    for (let i = 0; i < count; i++) this.rank[this.rankOrder[i] as number] = i;

    this.updatesTotal++;
  }

  // --- Accessors (hash / census / tests) -------------------------------------

  /** This update's weighted instant score for `cid`. */
  scoreAt(cid: ColonyId): number {
    return this.instant[cid] as number;
  }
  /** Raw subscore for `cid` on dimension `dim` (SCORE_DIM.*). */
  subscoreAt(cid: ColonyId, dim: ScoreDim): number {
    return this.subscore[cid * SCORE_DIM_COUNT + dim] as number;
  }
  /** Highest instant score `cid` has ever reached. */
  peakAt(cid: ColonyId): number {
    return this.peak[cid] as number;
  }
  /** Running sum of every instant score for `cid`. */
  lifetimeAt(cid: ColonyId): number {
    return this.lifetime[cid] as number;
  }
  /** The ranked total for `cid` (instant + amortised lifetime). */
  totalAt(cid: ColonyId): number {
    return this.total[cid] as number;
  }
  /** Standing of `cid` (0 == leader). */
  rankAt(cid: ColonyId): number {
    return this.rank[cid] as number;
  }
  /** The colony currently in first place, or NIL when there are no colonies. */
  leaderId(): ColonyId {
    const count = this.colonies.count;
    if (count <= 0) return NIL;
    for (let cid = 0; cid < count; cid++) {
      if ((this.rank[cid] as number) === 0) return cid;
    }
    return NIL;
  }
  /** Number of recompute passes performed (deterministic; for hashing). */
  get updates(): number {
    return this.updatesTotal;
  }
}
